import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import Barcode from 'react-barcode';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import QRCode from 'react-qr-code';

import { company } from '~/company/Company';
import Icon from '~/components/Icon/Icon';
import { useGlobal } from '~/hooks/useGlobal';

import { userStore } from '~/stores';

import type { OrderQrCodeProps } from './interfaces';
import styles from './OrderQrCode.module.scss';

const SMALL_SIZE = 146;
const FULLSCREEN_SIZE = 268.8;

const OrderQrCode = ({ className, orderNumber }: OrderQrCodeProps) => {
  const { t } = useTranslation();

  const { isSSR } = useGlobal();
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);

  const toggleFullscreen = () => setIsFullscreen((prev) => !prev);

  useEffect(() => {
    const removeClass = () => {
      document.body.classList.remove(`${styles.noScroll}`);
    };

    removeClass();
    if (isFullscreen) {
      document.body.classList.add(`${styles.noScroll}`);
    }

    return () => {
      removeClass();
    };
  }, [isFullscreen]);

  const showLabel = company.useBarcode
    ? t('deliveryMethods:showBarcodeInTheStore')
    : t('deliveryMethods:showInTheStore');

  const component = (
    <div
      className={classes([
        styles.root,
        className,
        isFullscreen && styles.fullscreen,
      ])}
    >
      {isFullscreen && (
        <div className={styles.closeWr}>
          <span
            role="button"
            className={styles.close}
            onClick={toggleFullscreen}
          >
            <Icon type="plus" className="icon__rotate-45" size={24} />
          </span>
        </div>
      )}
      <div className={styles.qrWr}>
        <div
          className={classes([
            styles.qr,
            isFullscreen && styles.qr__fs,
            company.useBarcode && styles.barcode,
          ])}
        >
          {company.useBarcode ? (
            <Barcode
              value={orderNumber}
              width={isFullscreen ? 2 : 1.5}
              height={isFullscreen ? FULLSCREEN_SIZE * 0.6 : SMALL_SIZE * 0.6}
              displayValue={false}
            />
          ) : (
            <QRCode
              role="img"
              value={orderNumber}
              size={isFullscreen ? FULLSCREEN_SIZE : SMALL_SIZE}
            />
          )}
        </div>
        {isFullscreen && !company.useBarcode && <p>{showLabel}</p>}
      </div>
      <div
        className={classes([
          styles.action,
          company.useBarcode && styles.withBarcode,
        ])}
      >
        {isFullscreen && <p>{showLabel}</p>}
        {!isFullscreen && (
          <div className={styles.label}>
            <p>{showLabel}</p>
          </div>
        )}
        <div className={styles.buttonWr}>
          <button
            type="button"
            className={classes([
              'button _bordered',
              isFullscreen && styles.closeExpandBtn,
              !isFullscreen && styles.expandBtn,
            ])}
            onClick={toggleFullscreen}
          >
            {isFullscreen ? t('close') : <Icon type="expand" size={24} />}
          </button>
        </div>
      </div>
    </div>
  );

  if (isFullscreen && !isSSR) {
    return createPortal(
      <div
        className={styles.orderQrCodePortal}
        dir={userStore.dir}
        data-company={company.name}
      >
        {component}
      </div>,
      document.body,
    );
  }
  return component;
};

export default observer(OrderQrCode);
